import { useCallback, useRef } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';

export const useAdminApi = () => {
  const { token, logout } = useAdminAuth();
  const logoutTimer = useRef(null);

  const apiCall = useCallback(async (url, options = {}) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      ...options.headers
    };

    // Don't set Content-Type for FormData (browser sets it with boundary)
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const res = await fetch(url, { ...options, headers });

    if (res.status === 401) {
      // Debounce logout so multiple concurrent 401s don't cause issues
      if (!logoutTimer.current) {
        logoutTimer.current = setTimeout(() => {
          logout();
          logoutTimer.current = null;
        }, 500);
      }
      throw new Error('Session expired');
    }

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || `Request failed (${res.status})`);
    }
    return data;
  }, [token, logout]);

  const get = useCallback((url) => apiCall(url, { method: 'GET' }), [apiCall]);

  const post = useCallback((url, body) => apiCall(url, {
    method: 'POST',
    body: body instanceof FormData ? body : JSON.stringify(body)
  }), [apiCall]);

  return { get, post, apiCall };
};
